/**
 * Paged, filtered loading for a Bexio workspace list.
 *
 * Both list endpoints have the same contract — `limit`/`offset` in, `{items,
 * total}` out, `total` counted over the same filters — so both pages can share
 * the fetch/pagination/race handling and differ only in their filter shape.
 *
 * STALE RESPONSES. Filters change faster than requests return: type "RE-0", then
 * "RE-00", and the first response can land after the second. Every request gets a
 * monotonic id and only the newest one is allowed to write state, so an older
 * response cannot repaint the table with rows that no longer match what the user
 * sees in the filter bar. (`AbortController` would also work, but ignoring the
 * result is enough here and keeps the fetch helpers untouched.)
 *
 * OFFSET RESET is the caller's job, not this hook's: only the page knows which of
 * its filters change the result set (a search term does) versus which do not
 * (opening a drawer). `setFilters` therefore takes the whole next filter object,
 * and the page resets `offset` alongside it.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export function useBexioList({ fetcher, filters, limit, offset }) {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Newest request wins. Incremented per request, compared on arrival.
  const generation = useRef(0);
  // `filters` is a fresh object every render; serialising it keeps the effect
  // from re-firing when nothing actually changed.
  const filterKey = JSON.stringify(filters ?? {});

  const load = useCallback(async () => {
    const mine = ++generation.current;
    setLoading(true);
    setError("");
    try {
      const res = await fetcher({ ...(filters ?? {}), limit, offset });
      if (mine !== generation.current) return; // superseded
      setItems(Array.isArray(res?.items) ? res.items : []);
      setTotal(typeof res?.total === "number" ? res.total : null);
    } catch (e) {
      if (mine !== generation.current) return;
      setItems([]);
      setTotal(null);
      setError(e?.message || "Daten konnten nicht geladen werden.");
    } finally {
      if (mine === generation.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher, filterKey, limit, offset]);

  useEffect(() => {
    load();
  }, [load]);

  return { items, total, loading, error, reload: load };
}

export default useBexioList;
